import { useGwsFetch, useScrmFetch } from "@/hooks/common/fetch/useFetch";
import { shopifyConst } from "@/hooks/shopify-hooks/useShopifyConst";
import { __SOURCE_TYPE_WEB_SITE__, __SERVICE_TYPE_SERVICE_REQUEST__, SERVICE_SUB_TYPES } from "@/const/iserviceConst";

const brand = process.env.BRAND.toLocaleUpperCase();
const site = document.body.dataset.site;
const { language } = document.body.dataset;

const { domain } = shopifyConst;
const urlMap = {
  MIDEA: 'https://www.midea.com',
  TOSHIBA: 'https://www.toshiba-lifestyle.com',
};

const buildQuery = (params) => {
  return Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
};

/**
 * 处理 serviceType 入参
 * - 当 serviceType 为 Inspection/Installation/Repair/Inquiry/Complaint 时：
 *   type 统一传 Service Request，原值通过 subType 传出
 * - 当 serviceType 为 Product Registration/Service Request 时：保持原样，subType 为 null
 * @param {string} serviceType 形参传入的服务类型
 * @returns {{ type: string, subType: string|null }}
 */
const resolveServiceType = (serviceType) => {
  if (SERVICE_SUB_TYPES.includes(serviceType)) {
    return { type: __SERVICE_TYPE_SERVICE_REQUEST__, subType: serviceType };
  }
  return { type: serviceType, subType: null };
};

export const useIServiceDivision = async (needLanguage) => {
  const baseParams = `query-country=${site}&query-url=${urlMap[brand]}`;
  const query = needLanguage ? `${baseParams}&query-language=${language}` : baseParams;
  return await useGwsFetch(`/iService/${site}/service/division?${query}`).get().json();
}

export const useIServiceServiceLabelApi = async ({
  divisionCode
}) => {
  const query = buildQuery({
    'query-divisionCode': divisionCode,
    'query-language': language
  });
  return useGwsFetch(`/iService/${site}/service/label?${query}`).get().json();
}

export const useIServiceCategoryList = async ({
  divisionCode
}) => {
  const { idcpLanguage } = document.body.dataset;
  return await useGwsFetch(`/iService/${site}/product/category/category/root?query-brand=${brand}&query-divisionCode=${divisionCode}&idcpLanguage=${idcpLanguage}`).get().json();
}

export const useIServiceModelList = async ({
  divisionCode, modelName, buId
}) => {
  const query = JSON.stringify({
    brand,
    divisionCode,
    modelName,
    buId
  });
  return await useGwsFetch(`/iService/${site}/product/model`).post({
    query
  }).json();
}

export const useIServiceModelNewApi = async ({
  divisionCode, modelName, serviceType
}, config = {}) => {
  // 转换 serviceType，细分类型走 Service Request + serviceSubType
  const { type: resolvedType, subType } = resolveServiceType(serviceType);
  const query = JSON.stringify({
    language,
    brand,
    divisionCode,
    modelName,
    serviceType: resolvedType,
    ...(subType ? { serviceSubType: subType } : {}),
    sourceType: __SOURCE_TYPE_WEB_SITE__
  });
  return useGwsFetch(`/iService/${site}/product/modelNew`, config).post({
    query
  }).json();
}

export const useIServiceCategoryRootNewApi = async ({
  divisionCode, serviceType
}) => {
  const { idcpLanguage } = document.body.dataset;
  // 转换 serviceType，细分类型走 Service Request + query-serviceSubType
  const { type: resolvedType, subType } = resolveServiceType(serviceType);
  const query = buildQuery({
    idcpLanguage,
    'query-brand': brand,
    'query-divisionCode': divisionCode,
    'query-serviceType': resolvedType,
    ...(subType ? { 'query-serviceSubType': subType } : {}),
    'query-sourceType': __SOURCE_TYPE_WEB_SITE__,
    'query-language': language
  });
  return useGwsFetch(`/iService/${site}/product/category/category/rootNew?${query}`).get().json();
}

export const useIServiceSubCategoryNewApi = async ({
  divisionCode, categoryId, serviceType
}) => {
  const { idcpLanguage } = document.body.dataset;
  // 转换 serviceType，细分类型走 Service Request + query-serviceSubType
  const { type: resolvedType, subType } = resolveServiceType(serviceType);
  const query = buildQuery({
    idcpLanguage,
    'query-brand': brand,
    'query-divisionCode': divisionCode,
    'query-parentId': categoryId,
    'query-serviceType': resolvedType,
    ...(subType ? { 'query-serviceSubType': subType } : {}),
    'query-sourceType': __SOURCE_TYPE_WEB_SITE__,
    'query-isContainUnPublish': 0,
    'query-language': language
  });
  return useGwsFetch(`/iService/${site}/product/category/category/subNew?${query}`).get().json();
}

export const useProductMsg = async ( divisionCode, snCode ) => {
  return await useScrmFetch(`/api/iscrm-redemption/website/redemption/campaign/checkSN?division=${divisionCode}&serialNo=${snCode}`).get().json();
}

export const useIserviceRegisteredProduct = async ({
  email,
  divisionCode
}) => {
  return await useScrmFetch(`/api/gws-ext/customer/registerproductsByIService?email=${email}&endpoint=${domain}&divisionCode=${divisionCode}`).post().json();
}

/**
 * 获取地址配置（动态表单字段列表 + 拼接规则）
 * @param {Object} params
 * @param {string} params.language - 语言标识，如 'en'、'zh'，或特殊值如 '85'（hk 站点）
 * @param {string} params.countryCode - 国家编码，如 'CN'、'US'
 * @returns {Promise} 返回地址配置数据，包含 itemList（字段列表）和 assemblyRule（拼接规则）
 */
export const useGetAddressConfig = ({ language: lang, countryCode }) => {
  return useGwsFetch(`/iService/${site}/address/getAddressConfig`).post({
    query: JSON.stringify({ language: lang || language, countryCode }),
  }).json();
};

/**
 * 模糊查询上层行政区划数据（省市区联动时，输入关键字搜索用）
 * @param {Object} params
 * @param {string} params.countryCode - 国家编码，限定搜索范围
 * @param {string} params.nameLoc - 搜索关键字（地区名称）
 * @param {number|string} params.level - 行政区划层级，如 1=省、2=市、3=区、4=街道
 * @returns {Promise} 返回匹配的行政区划数据，按上级到下级分组
 */
export const useGetParentRegionToCountry = ({ countryCode, nameLoc, level }) => {
  return useGwsFetch(`/iService/${site}/address/baseRegionRest/getParentRegionToCountry`).post({
    query: JSON.stringify({ countryCode, nameLoc, level }),
  }).json();
};

/**
 * 根据父级编码获取省市区县数据（级联加载下级区域列表）
 * @param {Object} params
 * @param {string} params.parentCode - 父级行政区划编码，如国家编码或省级编码
 * @returns {Promise} 返回子级行政区划列表，每项包含 nameEnu、nameLoc、code 等字段
 */
export const useGetRegionDataSource = ({ parentCode }) => {
  return useGwsFetch(`/iService/${site}/region`).post({
    query: JSON.stringify({ parentCode }),
  }).json();
};

/**
 * 根据 zipcode 反查地址（输入邮编后自动填充省市区信息）
 * @param {Object} params
 * @param {string} params.zipcode - 邮政编码，如 '100000' 或 '12345678'（BR 站点为8位）
 * @param {string} params.countryCode - 国家编码，用于限定查询范围
 * @returns {Promise} 返回匹配的地址数据列表，包含省市区层级信息
 */
export const useGetZipCodeAddress = ({ zipcode, countryCode }) => {
  return useGwsFetch(`/iService/${site}/address/getZipCodeAddress`).post({
    query: JSON.stringify({ zipcode, countryCode, language }),
  }).json();
};